import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { BRAND_COLORS } from 'brand-guidelines/colors';
import { TYPOGRAPHY } from 'brand-guidelines/typography';
import { MarketingButton } from 'common/styles/oc-styled-components';

const CARD_PADDING_DESKTOP = {
  top: 24,
  right: 24,
  bottom: 24,
  left: 24,
};

const CARD_PADDING_MOBILE = {
  top: 20,
  right: 20,
  bottom: 20,
  left: 20,
};

// How much the images stick out of the top of the card
const CARD_IMAGES_OFFSET_DESKTOP = 18;
const CARD_IMAGES_OFFSET_MOBILE = 15;

const getCardPadding = ({ isMobile }) => {
  const { top, right, bottom, left } = isMobile
    ? CARD_PADDING_MOBILE
    : CARD_PADDING_DESKTOP;
  return css`
    padding: ${top}px ${right}px ${bottom}px ${left}px;
  `;
};

const getImagesOffset = ({ isMobile }) => {
  const { top } = isMobile ? CARD_PADDING_MOBILE : CARD_PADDING_DESKTOP;
  const offset = isMobile
    ? CARD_IMAGES_OFFSET_MOBILE
    : CARD_IMAGES_OFFSET_DESKTOP;

  return -top - offset;
};

export const ResultCard = styled(Link)`
  position: relative;
  display: block;

  border-width: 1px 1px 0 1px;
  border-style: solid;
  border-color: ${BRAND_COLORS.stroke};
  border-radius: ${({ isMobile }) => (isMobile ? '8px' : '8px 8px 16px 16px')};

  ${({ isMapView }) =>
    isMapView
    && css`
      border-radius: 0;
      border: 0;
    `};

  background-color: ${BRAND_COLORS.white};

  transition-property: border-color, box-shadow;
  transition-duration: 0.1s;
  transition-timing-function: ease-in-out;

  &:hover,
  &:focus-within {
    cursor: pointer;
    ${({ hoverable, isMapView }) =>
    hoverable
    && !isMapView
    && css`
      border-color: transparent;
      box-shadow: inset 0 0 0 2px ${BRAND_COLORS.blue};
      `}
  }
`;

export const CardHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export const CardHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  /* Not strictly necessary, but confirms that text won't overlap profile images */
  flex-grow: 1;
`;

export const CardHeaderTitle = styled.h1`
  ${TYPOGRAPHY.CIRCULAR.black};
  font-size: 22px;
  line-height: 22px;
  margin: 0;

  ${({ isMobile }) =>
    isMobile
    && css`
      font-size: 18px;
      line-height: 20px;
    `}
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  width: 100%;
  ${getCardPadding};
`;

export const CardFooter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  padding: 8px 24px;

  color: ${BRAND_COLORS.white};

  background-color: ${BRAND_COLORS.blue};
  border-radius: 0 0 16px 16px;

  ${({ isMobile }) =>
    isMobile
    && css`
      border-radius: 0 0 8px 8px;
      padding: 8px 16px;
    `}

  ${({ isMapView }) =>
    isMapView
    && css`
      border-radius: 0;
    `}
`;

export const CardButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const CardButton = styled(MarketingButton)`
  && {
    padding: 7px 16px;

    ${TYPOGRAPHY.CIRCULAR.bold};
    line-height: 20px;
    border-radius: 24px;

    border: 0;
    color: ${BRAND_COLORS.blue};
    background: ${BRAND_COLORS.white};

    &:hover,
    &:focus {
      color: ${BRAND_COLORS.lightGreen};
      background: ${BRAND_COLORS.white};
    }
  }
`;

export const IconButton = styled(CardButton)`
  && {
    padding: 9px;
    border-radius: 50%;
    line-height: 0;

    & > .anticon {
      line-height: inherit;
    }
  }
`;

export const SuperPracticeBadgeContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${({ $center }) => $center && css`
    justify-content: center;
  `}
  gap: 10px;

  margin-bottom: 6px;

  ${TYPOGRAPHY.CIRCULAR.bold};
  color: ${BRAND_COLORS.red};
  text-transform: uppercase;
  font-size: ${(props) => (props.size === 'large' ? '16px' : '14px')};
`;

export const SuperPracticeBadge = styled.div`
  height: ${(props) => (props.size === 'large' ? '40px' : '25px')};
  width: ${(props) => (props.size === 'large' ? '40px' : '25px')};
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const ProfileImagesWrapper = styled.div`
  position: relative;
  top: ${getImagesOffset}px;
  flex-shrink: 0;

  /* This stops the sibling element matching the image height without affecting this element */
  height: 1px;
`;

export const NumberCircle = styled.div`
  ${({ isMapView }) =>
    isMapView
    && css`
      display: none;
    `};

  position: absolute;
  background-color: ${BRAND_COLORS.blue};
  border-radius: 50%;

  ${TYPOGRAPHY.OPEN_SANS.semiBold};
  color: ${BRAND_COLORS.white};
  text-align: center;

  ${({ isMobile }) => {
    const fontSize = isMobile ? 13 : 16;
    const size = isMobile ? 22 : 30;
    const offset = size / 2;

    return css`
      top: -${offset}px;
      left: -${offset}px;
      font-size: ${fontSize}px;
      line-height: ${size}px;
      height: ${size}px;
      width: ${size}px;
    `;
  }};
`;
